/**
 * UserDetailEditor — Full-page user detail/editor
 *
 * Collapsible sections: Profile, Roles & Access, Security
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Autocomplete,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import FullPageDetailTemplate from "templates/FullPageDetailTemplate";
import CollapsibleSection from "templates/FullPageDetailTemplate/CollapsibleSection";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import useUsers from "hooks/useUsers";
import useRoles from "hooks/useRoles";
import logger from "services/logger";

function UserDetailEditor() {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const ability = useAbility();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { getUser, updateUser, suspendUser, unsuspendUser } = useUsers();
  const { roles: availableRoles } = useRoles({ autoFetch: true });

  // User data
  const [userData, setUserData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    roles: false,
    security: false,
  });
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", title: "", message: "" });

  // Permissions
  const canRead = ability.can(ACTIONS.READ, SUBJECTS.USER);
  const canUpdate = ability.can(ACTIONS.MANAGE, SUBJECTS.USER) || ability.can(ACTIONS.UPDATE, SUBJECTS.USER);

  // Dirty tracking
  const hasChanges = useMemo(() => {
    if (!userData || !originalData) return false;
    return JSON.stringify(userData) !== JSON.stringify(originalData);
  }, [userData, originalData]);

  // Load
  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getUser(userId);
      if (!data) throw new Error("User not found");
      const normalized = {
        ...data,
        roles: Array.isArray(data.roles) ? data.roles : [],
      };
      setUserData(normalized);
      setOriginalData(JSON.parse(JSON.stringify(normalized)));
    } catch (err) {
      logger.error("[UserDetailEditor]", "Load failed:", err.message);
      showSnackbar("error", t("common.error"), err.message);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, getUser, t]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Field update
  const updateField = useCallback((field, value) => {
    setUserData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Save
  const handleSave = async () => {
    if (!userData || !hasChanges) return;
    setSaving(true);
    try {
      await updateUser(userId, {
        displayName: userData.displayName,
        role: userData.role,
        roles: userData.roles,
        activeRole: userData.activeRole,
        adminAccess: userData.adminAccess,
        roleAccess: userData.roleAccess,
      });
      setOriginalData(JSON.parse(JSON.stringify(userData)));
      showSnackbar("success", t("common.success"), t("users.detail.saved"));
    } catch (err) {
      logger.error("[UserDetailEditor]", "Save failed:", err.message);
      showSnackbar("error", t("common.error"), err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset
  const handleReset = () => {
    setUserData(JSON.parse(JSON.stringify(originalData)));
  };

  // Suspend/Unsuspend
  const handleSuspendToggle = async () => {
    try {
      if (userData.suspended) {
        await unsuspendUser(userId);
        updateField("suspended", false);
      } else {
        await suspendUser(userId);
        updateField("suspended", true);
      }
      showSnackbar("success", t("common.success"),
        userData.suspended ? t("users.detail.unsuspended") : t("users.detail.suspendedMsg"));
    } catch (err) {
      logger.error("[UserDetailEditor]", "Suspend toggle failed:", err.message);
      showSnackbar("error", t("common.error"), err.message);
    }
  };

  // Section toggle
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Snackbar
  const showSnackbar = (severity, title, message) => {
    setSnackbar({ open: true, severity, title, message });
  };

  // Styling
  const labelSx = {
    color: darkMode ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
    fontSize: "0.75rem",
    fontWeight: 600,
    mb: 0.5,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const valueSx = {
    color: darkMode ? "white" : "text.main",
    fontSize: "0.95rem",
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      borderRadius: "12px",
      "& fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)" },
      "&:hover fieldset": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
    },
    "& .MuiInputLabel-root": { color: darkMode ? "rgba(255,255,255,0.6)" : undefined },
    "& .MuiOutlinedInput-input": { color: darkMode ? "white" : undefined },
  };

  // Build chips
  const chips = useMemo(() => {
    if (!userData) return [];
    const c = [];
    if (userData.activeRole) c.push({ label: userData.activeRole, bgcolor: "rgba(13,148,136,0.25)", color: "#5EEAD4" });
    if (userData.suspended) c.push({ label: t("users.suspended"), bgcolor: "rgba(239,68,68,0.25)", color: "#FCA5A5" });
    return c;
  }, [userData, t]);

  // Role name options for autocomplete
  const roleOptions = useMemo(
    () => availableRoles.map((r) => r.name || r.id),
    [availableRoles]
  );

  // Access level derived from roleAccess[activeRole]
  const effectiveAccessLevel = useMemo(() => {
    if (!userData) return "—";
    const ra = userData.roleAccess || {};
    return ra[userData.activeRole] || userData.adminAccess || "—";
  }, [userData]);

  // Dynamic access level options based on active role
  const accessLevelOptions = useMemo(() => {
    if (!userData?.activeRole) return ["standard", "readonly"];
    const role = availableRoles.find((r) => (r.name || r.id) === userData.activeRole);
    if (role?.accessLevels) {
      return Object.keys(role.accessLevels);
    }
    const defaults = {
      admin: ["superadmin", "platformadmin", "standard", "readonly"],
      user: ["standard", "readonly"],
    };
    return defaults[userData.activeRole] || ["standard", "readonly"];
  }, [userData?.activeRole, availableRoles]);

  // Update access level — syncs both adminAccess and roleAccess
  const handleAccessLevelChange = useCallback((newLevel) => {
    setUserData((prev) => {
      const activeRole = prev.activeRole;
      const updatedRoleAccess = { ...(prev.roleAccess || {}), [activeRole]: newLevel };
      return { ...prev, adminAccess: newLevel, roleAccess: updatedRoleAccess };
    });
  }, []);

  // Build stats
  const stats = useMemo(() => {
    if (!userData) return [];
    return [
      { label: t("common.role"), value: userData.activeRole || "—" },
      { label: t("users.accessLevel"), value: effectiveAccessLevel },
      { label: t("roles.title"), value: (userData.roles || []).length },
    ];
  }, [userData, t, effectiveAccessLevel]);

  // Actions
  const actionButtons = (
    <>
      <Tooltip title={t("roles.detail.refresh")}>
        <IconButton onClick={loadUser} sx={{ color: "white" }}>
          <RefreshIcon />
        </IconButton>
      </Tooltip>

      {canUpdate && hasChanges && (
        <>
          <KDButton
            variant="outlined"
            size="small"
            onClick={handleReset}
            sx={{
              color: "white",
              borderColor: "rgba(255,255,255,0.4)",
              "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            {t("roles.detail.discard")}
          </KDButton>
          <KDButton
            variant="contained"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
            }}
          >
            {saving ? (
              <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
            ) : (
              <SaveIcon sx={{ fontSize: 18, mr: 0.5 }} />
            )}
            {saving ? t("common.loading") : t("common.save")}
          </KDButton>
        </>
      )}

      {canUpdate && userData && (
        <Tooltip title={userData.suspended ? t("users.unsuspend") : t("users.suspend")}>
          <IconButton
            onClick={handleSuspendToggle}
            sx={{
              color: userData.suspended ? "#86EFAC" : "#FCA5A5",
              "&:hover": { color: userData.suspended ? "#22C55E" : "#EF4444" },
            }}
          >
            {userData.suspended ? <CheckCircleIcon /> : <BlockIcon />}
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <FullPageDetailTemplate
      title={userData?.displayName || userData?.email || userId || ""}
      subtitle={userData?.email || ""}
      headerColor="primary"
      backRoute="/admin/users"
      breadcrumbLabel={userData?.email || null}
      loading={loading}
      notFound={!loading && !userData}
      notFoundMessage={t("users.detail.notFound")}
      permissionDenied={!canRead}
      chips={chips}
      stats={stats}
      alerts={
        userData?.suspended
          ? [{ severity: "warning", content: <><strong>{t("users.suspended")}:</strong> {t("users.detail.suspendedAlert")}</> }]
          : []
      }
      actions={userData ? actionButtons : null}
      snackbar={snackbar}
      onSnackbarClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
    >
      {userData && (
        <>
          {/* Profile Section */}
          <CollapsibleSection
            title={t("users.detail.profile")}
            icon={<PersonIcon sx={{ color: "white", fontSize: 20 }} />}
            iconGradient={["#0EA5E9", "#7DD3FC"]}
            expanded={expandedSections.profile}
            onToggle={() => toggleSection("profile")}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <KDTypography sx={labelSx}>{t("users.detail.uid")}</KDTypography>
                <KDTypography sx={valueSx}>{userData.uid || "—"}</KDTypography>
                <KDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
                  {t("roles.detail.cannotChange")}
                </KDTypography>
              </Grid>

              <Grid item xs={12} md={4}>
                <KDTypography sx={labelSx}>{t("users.displayName")}</KDTypography>
                {canUpdate ? (
                  <TextField
                    size="small"
                    fullWidth
                    value={userData.displayName || ""}
                    onChange={(e) => updateField("displayName", e.target.value)}
                    sx={inputSx}
                  />
                ) : (
                  <KDTypography sx={valueSx}>{userData.displayName || "—"}</KDTypography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <KDTypography sx={labelSx}>{t("common.email")}</KDTypography>
                <KDTypography sx={valueSx}>{userData.email || "—"}</KDTypography>
              </Grid>

              <Grid item xs={12} md={4}>
                <KDTypography sx={labelSx}>{t("users.createdAt")}</KDTypography>
                <KDTypography sx={valueSx}>
                  {userData.createdAt
                    ? new Date(
                        userData.createdAt._seconds
                          ? userData.createdAt._seconds * 1000
                          : userData.createdAt
                      ).toLocaleDateString()
                    : "—"}
                </KDTypography>
              </Grid>
            </Grid>
          </CollapsibleSection>

          {/* Roles & Access Section */}
          <CollapsibleSection
            title={t("users.detail.rolesAccess")}
            icon={<AdminPanelSettingsIcon sx={{ color: "white", fontSize: 20 }} />}
            iconGradient={["#8B5CF6", "#C4B5FD"]}
            badge={(userData.roles || []).length}
            expanded={expandedSections.roles}
            onToggle={() => toggleSection("roles")}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <KDTypography sx={labelSx}>{t("users.detail.activeRole")}</KDTypography>
                {canUpdate ? (
                  <Autocomplete
                    value={userData.activeRole || ""}
                    options={roleOptions}
                    onChange={(_e, val) => updateField("activeRole", val || "")}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={inputSx} />
                    )}
                    freeSolo
                  />
                ) : (
                  <KDTypography sx={valueSx}>{userData.activeRole || "—"}</KDTypography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <KDTypography sx={labelSx}>{t("users.accessLevel")}</KDTypography>
                {canUpdate ? (
                  <Autocomplete
                    value={effectiveAccessLevel === "—" ? "" : effectiveAccessLevel}
                    options={accessLevelOptions}
                    onChange={(_e, val) => handleAccessLevelChange(val || "")}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={inputSx} />
                    )}
                    freeSolo
                  />
                ) : (
                  <KDTypography sx={valueSx}>{effectiveAccessLevel}</KDTypography>
                )}
              </Grid>

              <Grid item xs={12}>
                <KDTypography sx={labelSx}>{t("users.detail.assignedRoles")}</KDTypography>
                {canUpdate ? (
                  <Autocomplete
                    multiple
                    value={userData.roles || []}
                    options={roleOptions}
                    onChange={(_e, val) => updateField("roles", val)}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={inputSx} placeholder={t("users.detail.selectRoles")} />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          size="small"
                          color="primary"
                          variant="outlined"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                  />
                ) : (
                  <KDBox display="flex" gap={0.5} flexWrap="wrap">
                    {(userData.roles || []).map((r) => (
                      <Chip key={r} label={r} size="small" color="primary" variant="outlined" />
                    ))}
                    {(userData.roles || []).length === 0 && (
                      <KDTypography sx={valueSx}>—</KDTypography>
                    )}
                  </KDBox>
                )}
              </Grid>
            </Grid>
          </CollapsibleSection>

          {/* Security Section */}
          <CollapsibleSection
            title={t("users.detail.security")}
            icon={<SecurityIcon sx={{ color: "white", fontSize: 20 }} />}
            iconGradient={["#EF4444", "#FCA5A5"]}
            expanded={expandedSections.security}
            onToggle={() => toggleSection("security")}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <KDTypography sx={labelSx}>{t("common.status")}</KDTypography>
                <Chip
                  label={userData.suspended ? t("users.suspended") : t("common.active")}
                  size="small"
                  color={userData.suspended ? "error" : "success"}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <KDTypography sx={labelSx}>{t("users.lastLogin")}</KDTypography>
                <KDTypography sx={valueSx}>
                  {userData.lastLogin
                    ? new Date(
                        userData.lastLogin._seconds
                          ? userData.lastLogin._seconds * 1000
                          : userData.lastLogin
                      ).toLocaleString()
                    : t("users.never")}
                </KDTypography>
              </Grid>

              {canUpdate && (
                <Grid item xs={12}>
                  <KDBox
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      border: userData.suspended
                        ? "1px solid rgba(239,68,68,0.3)"
                        : darkMode
                          ? "1px solid rgba(255,255,255,0.08)"
                          : "1px solid rgba(0,0,0,0.06)",
                      bgcolor: userData.suspended
                        ? darkMode ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)"
                        : "transparent",
                    }}
                  >
                    <KDBox display="flex" alignItems="center" justifyContent="space-between">
                      <KDBox>
                        <KDTypography variant="subtitle2" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                          {userData.suspended ? t("users.unsuspend") : t("users.suspend")}
                        </KDTypography>
                        <KDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
                          {userData.suspended
                            ? t("users.detail.unsuspendHint")
                            : t("users.detail.suspendHint")}
                        </KDTypography>
                      </KDBox>
                      <KDButton
                        variant="contained"
                        size="small"
                        onClick={handleSuspendToggle}
                        sx={{
                          backgroundColor: userData.suspended ? "#22C55E" : "#EF4444",
                          color: "white",
                          "&:hover": {
                            backgroundColor: userData.suspended ? "#16A34A" : "#DC2626",
                          },
                        }}
                      >
                        {userData.suspended ? t("users.unsuspend") : t("users.suspend")}
                      </KDButton>
                    </KDBox>
                  </KDBox>
                </Grid>
              )}
            </Grid>
          </CollapsibleSection>
        </>
      )}
    </FullPageDetailTemplate>
  );
}

export default UserDetailEditor;
